// utils/time.ts

/**
 * 时间范围接口
 */
export interface TimeRange {
  startTime: string; // 格式：YYYY-MM-DD HH:mm:ss
  endTime: string; // 格式：YYYY-MM-DD HH:mm:ss
}

/**
 * 解析日期字符串为 Date 对象
 * @param dateStr 格式：YYYY/MM/DD
 * @returns Date 对象
 * @throws 当日期格式不正确时抛出错误
 */
function parseDate(dateStr: string): Date {
  const parts = dateStr.split('/').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) {
    throw new Error(`无效的日期格式: ${dateStr}，请使用 YYYY/MM/DD 格式`);
  }
  const [year, month, day] = parts;
  // month 需要减 1，因为 Date 的月份从 0 开始
  return new Date(year, month - 1, day);
}

/**
 * 格式化日期为 YYYY-MM-DD HH:mm:ss
 * @param date Date 对象
 * @returns 格式化后的日期字符串
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * 获取时间范围
 *
 * @param type 时间类型
 *   - 0: 日  返回传入日期当天的 00:00:00 ~ 23:59:59
 *   - 1: 周  返回传入日期所在周（周一 ~ 周日）的 00:00:00 ~ 23:59:59
 *   - 2: 月  返回传入日期所在月的 00:00:00 ~ 23:59:59
 *   - 3: 自定义  返回 date ~ endDate 的 00:00:00 ~ 23:59:59
 * @param date 起始日期，格式 YYYY-MM-DD
 * @param endDate 结束日期，格式 YYYY-MM-DD，仅当 type = 3 时必传
 * @returns 时间范围对象 { startTime, endTime }
 *
 * @example
 * // 日
 * getTimeRange(0, '2026-09-03')
 * // => { startTime: '2026-09-03 00:00:00', endTime: '2026-09-03 23:59:59' }
 *
 * @example
 * // 周（周一 ~ 周日）
 * getTimeRange(1, '2026-09-03')
 * // => { startTime: '2026-08-31 00:00:00', endTime: '2026-09-06 23:59:59' }
 *
 * @example
 * // 月
 * getTimeRange(2, '2026-09-03')
 * // => { startTime: '2026-09-01 00:00:00', endTime: '2026-09-30 23:59:59' }
 *
 * @example
 * // 自定义
 * getTimeRange(3, '2026-08-06', '2026-09-03')
 * // => { startTime: '2026-08-06 00:00:00', endTime: '2026-09-03 23:59:59' }
 */
export function getTimeRange(type: number, date: string): TimeRange {
  let start: Date;
  let end: Date;

  switch (type) {
    case 0: {
      const d = parseDate(date);
      // ─── 日 ───
      start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
      end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
      break;
    }

    case 1: {
      const d = parseDate(date);
      // ─── 周（周一 ~ 周日） ───
      const dayOfWeek = d.getDay(); // 0=周日, 1=周一, ..., 6=周六
      // 计算到周一的天数差：周日差 6 天，其余差 dayOfWeek - 1 天
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(d);
      monday.setDate(d.getDate() - diffToMonday);
      monday.setHours(0, 0, 0, 0);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      start = monday;
      end = sunday;
      break;
    }

    case 2: {
      const d = parseDate(date);
      // ─── 月 ───
      start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0);
      // 下个月的第 0 天 = 当前月的最后一天
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
      break;
    }

    case 3: {
      start = new Date(date.split(',')[0]);
      end = new Date(date.split(',')[1]);
      break;
    }

    default: {
      throw new Error(`不支持的时间类型: ${type}`);
    }
  }

  return {
    startTime: formatDate(start),
    endTime: formatDate(end)
  };
}
